import { EyeIcon, EyeOffIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import type { DataError } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import styles from './form.module.css';

/**
 * The form plumbing both user forms need.
 *
 * There are two of them and there always will be, because the API has two shapes: an
 * administrator editing anybody through `UserEditCard`, and a person editing
 * themselves through `UserProfileCard` — which has no `role`, no `isActive` and no
 * `username`, so a privilege cannot be raised through it. Sharing the *form* between
 * them would undo that; sharing the parts a form is built from does not.
 *
 * In `features/users/` rather than `components/ui/` because only this feature uses
 * them. The moment a second feature does, they belong there instead.
 */

/** A Kazakh IIN or BIN: exactly twelve digits, nothing else. */
export const TIN_LENGTH = 12;

/** What the IIN input lets through: digits, and no more of them than an IIN has. */
export const sanitizeTin = (value: string): string =>
  value.replace(/\D/g, '').slice(0, TIN_LENGTH);

/**
 * A phone number: country code, then ten national digits — `+7 700 123 45 67`.
 *
 * The column is free text, so without a mask it collects `87001234567`,
 * `+7 (700) 123-45-67` and `700 123 45 67` for the same person and nothing can be
 * matched or dialled from it. This normalises as it is typed, so what is stored is
 * one shape.
 *
 * Two habits are absorbed rather than rejected, because they are how the number is
 * actually written here: a leading `8` is how a domestic call is dialled, and a
 * number is often given without its country code at all.
 *
 * **It assumes a Kazakh number** — one country digit and a 3-3-2-2 body. A foreign
 * number does not survive it. That is the trade for a uniform column on a system
 * whose users all work one operation; the day it needs to hold a London number,
 * this is the function to widen.
 */
export const PHONE_DIGITS = 11;

const digitsOf = (value: string): string => value.replace(/\D/g, '');

export const maskPhone = (value: string, previous = ''): string => {
  let digits = digitsOf(value);

  /*
    A separator was deleted, not a digit.

    Backspacing over the space in `+7 700` leaves `+7700`, whose digits are what
    they were — so re-masking would put the space straight back and the keystroke
    would do nothing. Taking a digit off instead is what the reader meant.
  */
  if (digits === digitsOf(previous) && value.length < previous.length) {
    digits = digits.slice(0, -1);
  }

  // `8` for `7` is how the same number is dialled domestically. Safe at any length:
  // the country code is 7, and no Kazakh national number begins with 8.
  if (digits.startsWith('8')) digits = `7${digits.slice(1)}`;
  /*
    Ten digits PASTED are a national number missing its country code.

    Only when pasted, and that distinction is the whole difficulty: ten digits is
    also what a half-typed full number looks like, so prefixing on every keystroke
    would turn `7700123456` — six characters from finished — into `+7 770 012 34 56`
    under the reader's hands. A jump of more than one character is the only signal
    available that the value arrived whole.

    It cannot be a test for a leading `7` instead: Kazakh mobile numbers START with
    7 — 700, 701, 705, 747, 777 — so that is exactly the common case, and reading
    the first 7 as the country code is what produced `+7 001 234 56 7`.
  */
  else if (digits.length === 10 && value.length - previous.length > 1) {
    digits = `7${digits}`;
  }

  digits = digits.slice(0, PHONE_DIGITS);
  if (digits === '') return '';

  const parts = /^(\d)(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/.exec(digits);
  if (!parts) return `+${digits}`;

  return [`+${parts[1]}`, parts[2], parts[3], parts[4], parts[5]].filter(Boolean).join(' ');
};

/**
 * Not the RFC, which would be both unreadable and wrong to enforce — the server is
 * the judge of what it accepts. This catches what a validator is actually for:
 * a value that is visibly not an address (no `@`, no domain, a stray space).
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * The contact rules both user forms share, settled before the round trip.
 *
 * Empty is never a problem: every contact field is optional, and clearing one is a
 * legal edit. The rules judge only what was actually typed —
 *
 * - the email has to look like an address;
 * - the phone has to be a complete number — the mask shapes it, this catches the
 *   half-typed one, which is the only way through the mask that is still wrong;
 * - the IIN/TIN is exactly {@link TIN_LENGTH} digits, or nothing.
 */
export const contactProblems = (
  draft: { email: string; phoneNumber: string; tin: string },
  t: (key: Key, values?: Record<string, string | number>) => string,
): Partial<Record<'email' | 'phoneNumber' | 'tin', string>> => {
  const problems: Partial<Record<'email' | 'phoneNumber' | 'tin', string>> = {};

  const email = draft.email.trim();
  if (email !== '' && !EMAIL_SHAPE.test(email)) {
    problems.email = t('users.panel.emailInvalid');
  }

  const phone = digitsOf(draft.phoneNumber);
  if (draft.phoneNumber.trim() !== '' && phone.length !== PHONE_DIGITS) {
    problems.phoneNumber = t('users.panel.phoneInvalid');
  }

  const tin = draft.tin.trim();
  if (tin !== '' && tin.length !== TIN_LENGTH) {
    problems.tin = t('users.panel.tinInvalid', { count: TIN_LENGTH });
  }

  return problems;
};

/** A value that cannot be edited, shown in the shape of a field so a form reads evenly. */
export const Readout = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    <span className={cx(styles.readout, mono && styles.readoutMono)}>{value}</span>
  </div>
);

/** The eye inside a password field. */
export const PasswordReveal = ({ shown, onToggle }: { shown: boolean; onToggle: () => void }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={styles.reveal}
      onClick={onToggle}
      aria-label={t(shown ? 'auth.hidePassword' : 'auth.showPassword')}
      title={t(shown ? 'auth.hidePassword' : 'auth.showPassword')}
      // Not a Tab stop: it reveals what was just typed and sits between the password
      // field and the next one.
      tabIndex={-1}
    >
      {shown ? <EyeOffIcon className={styles.revealIcon} /> : <EyeIcon className={styles.revealIcon} />}
    </button>
  );
};

/**
 * Spreads a failed write over the inputs that caused it.
 *
 * The server's `path` may be dotted (`userCard.email`), so only the last segment is
 * matched against `fieldOf`. Anything that matches nothing comes back as `loose` — an
 * error with no field to attach it to is still an error, and dropping it is what
 * silently ignoring an unrecognised path amounts to.
 */
export const attachErrors = <K extends string>(
  errors: readonly DataError[],
  fieldOf: Readonly<Record<string, K>>,
): { attached: Partial<Record<K, string>>; loose: string[] } => {
  const attached: Partial<Record<K, string>> = {};
  const loose: string[] = [];

  for (const error of errors) {
    const segment = error.path?.split('.').pop() ?? '';
    const field = fieldOf[segment];
    const detail = error.message ?? error.errorCode ?? '';

    if (detail === '') continue;
    if (field) attached[field] = detail;
    else loose.push(detail);
  }

  return { attached, loose };
};
